import React, { useState, useEffect } from 'react';
import { format, isAfter, isBefore, isEqual, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';

// Custom day component that shows the selected range with proper highlighting
const CustomPickersDay = (props) => {
  const { 
    day, 
    outsideCurrentMonth, 
    isFirstDay, 
    isLastDay, 
    isInRange, 
    ...other 
  } = props;
  
  const theme = useTheme();
  
  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={{
        fontWeight: isFirstDay || isLastDay ? 'bold' : 'medium',
        position: 'relative',
        ...(isFirstDay && {
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '50%',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
            zIndex: -1,
          }
        }),
        ...(isLastDay && {
          borderTopRightRadius: '50%',
          borderBottomRightRadius: '50%',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
            zIndex: -1,
          }
        }),
        ...(isInRange && !isFirstDay && !isLastDay && {
          borderRadius: 0,
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
          },
        }),
      }}
    />
  );
};

export default function DateRangePicker({ value, onChange, isLoading = false }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDay, setHoveredDay] = useState(null);
  
  // Create properly initialized dates
  const initFromDate = () => {
    if (value?.from) {
      const date = new Date(value.from);
      date.setHours(0, 0, 0, 0);
      return date;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };
  
  const initToDate = () => {
    if (value?.to) {
      const date = new Date(value.to);
      date.setHours(23, 59, 59, 999);
      return date;
    }
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  };
  
  const [dateRange, setDateRange] = useState({
    from: initFromDate(),
    to: initToDate(),
  });
  
  const [selectingEnd, setSelectingEnd] = useState(false);

  // Update internal state when props change
  useEffect(() => {
    if (value?.from && value?.to) {
      const fromDate = new Date(value.from);
      fromDate.setHours(0, 0, 0, 0);
      
      const toDate = new Date(value.to);
      toDate.setHours(23, 59, 59, 999);
      
      console.log("DateRangePicker received new props:", {
        from: fromDate.toString(),
        to: toDate.toString()
      });
      
      setDateRange({
        from: fromDate,
        to: toDate
      });
    }
  }, [value]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSelectingEnd(false);
    
    // Set current month to the month of the start date
    if (value?.from) {
      setCurrentMonth(value.from);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setHoveredDay(null);
  };

  const handleApply = () => {
    // Ensure we're setting the end date to the end of day
    // and handling timezone issues properly
    const from = startOfDay(dateRange.from);
    const to = endOfDay(dateRange.to);
    
    console.log("DateRangePicker applying dates:", {
      from: from.toString(),
      to: to.toString()
    });
    
    onChange({
      from: from,
      to: to
    });
    handleClose();
  };

  const handleDaySelect = (date) => {
    // Make sure we're working with a copy to avoid mutations
    const selectedDate = new Date(date);
    // Set to start of day
    selectedDate.setHours(0, 0, 0, 0);
    
    if (!selectingEnd) {
      // Selecting start date
      console.log("Selected start date:", selectedDate.toString());
      setDateRange({
        from: selectedDate,
        to: selectedDate,
      });
      setSelectingEnd(true);
    } else {
      // Selecting end date
      console.log("Selected end date:", selectedDate.toString());
      
      if (isBefore(selectedDate, dateRange.from)) {
        // If selected date is before start date, swap them
        const endDate = new Date(dateRange.from);
        endDate.setHours(23, 59, 59, 999);
        
        setDateRange({
          from: selectedDate,
          to: endDate, // Use end of day for end date
        });
      } else {
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);
        
        setDateRange({
          from: dateRange.from,
          to: endDate, // Use end of day for end date
        });
      }
      setSelectingEnd(false);
    }
  };

  const handleDayHover = (day) => {
    if (selectingEnd) {
      setHoveredDay(day);
    }
  };

  const handleMonthChange = (month) => {
    setCurrentMonth(month);
  };

  const renderDay = (day, _selectedDays, pickersDayProps) => {
    const dayDate = startOfDay(day);
    const fromDate = dateRange.from ? startOfDay(dateRange.from) : null;
    const toDate = dateRange.to ? startOfDay(dateRange.to) : null;
    
    // Check if this day is the start or end of the selected range
    const isFirstDay = fromDate && isEqual(dayDate, fromDate);
    const isLastDay = toDate && isEqual(dayDate, toDate);
    
    // For the hovering effect when selecting the end date
    let isInRange = false;
    
    if (fromDate && toDate) {
      // When both dates are selected
      isInRange = isWithinInterval(dayDate, { start: fromDate, end: toDate });
    } else if (fromDate && hoveredDay && selectingEnd) {
      // When selecting end date and hovering
      const hoverDate = startOfDay(hoveredDay);
      if (isBefore(fromDate, hoverDate)) {
        isInRange = isWithinInterval(dayDate, { start: fromDate, end: hoverDate });
      } else {
        isInRange = isWithinInterval(dayDate, { start: hoverDate, end: fromDate });
      }
    }
    
    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        day={day}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        isInRange={isInRange}
        onMouseEnter={() => handleDayHover(day)}
      />
    );
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  // Calculate the number of days in the selected range
  const getDayCount = () => {
    if (!dateRange.from || !dateRange.to) return 0;
    
    const diffTime = Math.abs(dateRange.to - dateRange.from);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <Box>
      <Button
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        disabled={isLoading}
        startIcon={<CalendarMonthIcon sx={{ color: theme.palette.primary.main }} />}
        endIcon={isLoading ? <CircularProgress size={16} /> : <KeyboardArrowDownIcon />}
        sx={{
          height: 36,
          borderRadius: '8px',
          borderColor: alpha(theme.palette.divider, 0.3),
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
          px: 1.5,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
          fontSize: '0.85rem',
        }}
      >
        {value?.from ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: '4px',
              px: 1,
              py: 0.25,
              mr: 0.5,
            }}>
              <Typography variant="body2" fontWeight="medium" color="primary.main">
                {format(value.from, 'MMM dd')}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mx: 0.5 }}>-</Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: '4px',
              px: 1,
              py: 0.25,
              ml: 0.5,
            }}>
              <Typography variant="body2" fontWeight="medium" color="primary.main">
                {format(value.to, 'MMM dd')}
              </Typography>
            </Box>
          </Box>
        ) : (
          'Select date range'
        )}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 0,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            width: 'auto',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff'
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* <Typography variant="subtitle1" fontWeight="600" color="text.primary">
              {selectingEnd ? 'Select End Date' : 'Select Start Date'}
            </Typography> */}
            <Chip
              label={`${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')} (${getDayCount()} days)`}
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 'medium',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </Box>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={selectingEnd ? dateRange.to : dateRange.from}
              onChange={handleDaySelect}
              onMonthChange={handleMonthChange}
              renderDay={renderDay}
              showDaysOutsideCurrentMonth
              displayWeekNumber={false}
              sx={{
                '& .MuiPickersCalendarHeader-root': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pl: 2,
                  pr: 2,
                },
                '& .MuiPickersCalendarHeader-label': {
                  fontWeight: 'bold',
                  fontSize: '1rem',
                },
                '& .MuiPickersArrowSwitcher-root': {
                  display: 'flex',
                },
                '& .MuiPickersArrowSwitcher-button': {
                  color: theme.palette.primary.main,
                },
                '& .MuiDayCalendar-header': {
                  justifyContent: 'space-around',
                  paddingX: 2,
                },
                '& .MuiDayCalendar-weekContainer': {
                  justifyContent: 'space-around',
                  margin: '2px 0',
                },
                '& .MuiPickersDay-root': {
                  width: 36,
                  height: 36,
                  fontSize: '0.875rem',
                  margin: '0 2px',
                },
                '& .MuiPickersDay-today': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.main,
                },
              }}
            />
          </LocalizationProvider>
          
          <Box sx={{ 
            p: 1.5, 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.9)
          }}>
            <Button 
              variant="contained" 
              onClick={handleApply}
              size="small"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                px: 3,
                py: 0.75,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
} 